import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API, BACKEND_URL } from "@/lib/api";



export default function Login() {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // "login" or "register"
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    // Fetch logo for branding
    const fetchLogo = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/branding/logo`);
        setLogo(res.data.logo_url);
      } catch (e) {
        console.error("Failed to fetch logo", e);
      }
    };
    fetchLogo();
  }, []);

  // AUTO LOGIN BYPASS
  const handleBypass = () => {
    const dummyUser = {
      id: "bypass_user",
      email: "info@rulera.de",
      name: "Admin (Bypassed)",
      role: "admin",
      is_admin: true
    };

    // Set dummy token 
    localStorage.setItem("auth_token", "bypass_token_active");
    localStorage.setItem("user", JSON.stringify(dummyUser));

    toast.success("Bypass aktiv - Leite weiter...");

    // Short delay to ensure localStorage is set before redirect
    setTimeout(() => {
      window.location.href = "/dashboard";
    }, 500);
  };

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const error = searchParams.get("error");
  const redirectTo = searchParams.get("redirect") || "/dashboard";

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: email.toLowerCase(),
        password
      });

      if (response.data.success) {
        // Save token and user
        localStorage.setItem("auth_token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));

        toast.success("Erfolgreich angemeldet!");

        // Redirect to dashboard
        window.location.href = redirectTo;
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Anmeldung fehlgeschlagen";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: email.toLowerCase(),
        password,
        name
      });

      if (response.data.success) {
        // Save token and user
        localStorage.setItem("auth_token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));

        toast.success("Registrierung erfolgreich!");

        // Redirect directly to dashboard (they get a free package by default)
        window.location.href = "/dashboard";
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Registrierung fehlgeschlagen";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setLoading(true);
    window.location.href = `${API}/auth/google/login?redirect_to=${redirectTo}`;
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-4 font-['Plus_Jakarta_Sans',_sans-serif]">
      {/* Background decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-accent/5 rounded-full blur-[100px] -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-brand-primary/5 rounded-full blur-[100px] -ml-32 -mb-32" />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-xl rounded-[3rem] overflow-hidden">
          <CardContent className="p-10 sm:p-12">
            {/* Logo */}
            <div className="text-center mb-10">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {logo ? (
                  <img
                    src={`${BACKEND_URL}${logo}`}
                    alt="Logo"
                    className="h-16 mx-auto mb-6 object-contain"
                  />
                ) : (
                  <div className="w-16 h-16 bg-brand-primary rounded-2xl mx-auto flex items-center justify-center mb-6 shadow-xl">
                    <span className="text-3xl text-white italic">M</span>
                  </div>
                )}
                <h1 className="text-3xl font-black text-brand-primary tracking-tight">
                  {mode === "login"
                    ? (language === 'de' ? 'Willkommen' : 'Welcome')
                    : (language === 'de' ? 'Konto erstellen' : 'Create account')
                  }
                </h1>
                <p className="text-brand-primary/40 font-bold uppercase tracking-[0.2em] text-[10px] mt-2">
                  Premium Event Planning
                </p>
              </motion.div>
            </div>

            {/* Google Login Button */}
            <div className="space-y-4 mb-10">
              <Button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full h-14 bg-white border border-brand-primary/10 text-brand-primary hover:bg-brand-background rounded-2xl font-bold flex items-center justify-center gap-3 shadow-md transition-all active:scale-[0.98]"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.43 1.95 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                  />
                </svg>
                {language === 'de' ? 'Mit Google anmelden' : 'Sign in with Google'}
              </Button>

              <div className="relative flex items-center justify-center">
                <div className="w-full h-px bg-brand-primary/5" />
                <span className="absolute px-4 bg-white text-[9px] font-black text-brand-primary/30 uppercase tracking-[0.3em]">
                  {language === 'de' ? 'Oder klassisch' : 'Or classic'}
                </span>
              </div>
            </div>

            {/* Mode Tabs */}
            <div className="flex mb-8 bg-brand-primary/5 rounded-2xl p-1.5 border border-brand-primary/5">
              <button
                onClick={() => setMode("login")}
                className={`flex-1 py-3 px-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-200 ${mode === "login"
                  ? "bg-white text-brand-primary shadow-md"
                  : "text-brand-primary/40 hover:text-brand-primary"
                  }`}
              >
                {language === 'de' ? 'Anmelden' : 'Login'}
              </button>
              <button
                onClick={() => setMode("register")}
                className={`flex-1 py-3 px-4 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-200 ${mode === "register"
                  ? "bg-white text-brand-primary shadow-md"
                  : "text-brand-primary/40 hover:text-brand-primary"
                  }`}
              >
                {language === 'de' ? 'Registrieren' : 'Register'}
              </button>
            </div>

            {/* Emergency Bypass for Local Dev */}
            {(window.location.hostname === 'localhost' ||
              window.location.hostname === '127.0.0.1' ||
              window.location.hostname.startsWith('192.168.') ||
              window.location.hostname.endsWith('.local')) && (
                <button
                  onClick={handleBypass}
                  className="w-full py-3 mb-6 bg-rose-500/10 text-rose-600 rounded-xl text-[11px] font-black uppercase tracking-[0.2em] hover:bg-rose-500 hover:text-white transition-all border border-rose-500/20 active:scale-[0.98] shadow-sm flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined text-lg">admin_panel_settings</span>
                  {language === 'de' ? 'Not-Login (Nur lokal)' : 'Emergency Login (Local only)'}
                </button>
              )}

            {/* Email/Password Form */}
            <form onSubmit={mode === "login" ? handleEmailLogin : handleRegister} className="space-y-6">
              {mode === "register" && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[9px] uppercase tracking-[0.3em] text-brand-primary/50 font-black ml-1">
                    {language === 'de' ? 'Vollständiger Name' : 'Full Name'}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Alex Mustermann"
                    required
                    className="h-14 rounded-2xl bg-brand-primary/5 border-none font-bold text-brand-primary placeholder:text-brand-primary/20 focus-visible:ring-brand-accent"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[9px] uppercase tracking-[0.3em] text-brand-primary/50 font-black ml-1">
                  {language === 'de' ? 'E-Mail Adresse' : 'Email Address'}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  required
                  className="h-14 rounded-2xl bg-brand-primary/5 border-none font-bold text-brand-primary placeholder:text-brand-primary/20 focus-visible:ring-brand-accent"
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password" className="text-[9px] uppercase tracking-[0.3em] text-brand-primary/50 font-black ml-1">
                    {language === 'de' ? 'Passwort' : 'Password'}
                  </Label>
                  {mode === "login" && (
                    <Link to="/forgot-password" className="text-[10px] font-black uppercase tracking-widest text-brand-accent hover:text-brand-primary transition-colors">
                      Vergessen?
                    </Link>
                  )}
                </div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-14 rounded-2xl bg-brand-primary/5 border-none font-bold text-brand-primary placeholder:text-brand-primary/20 focus-visible:ring-brand-accent"
                />
              </div>

              {mode === "register" && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-[9px] uppercase tracking-[0.3em] text-brand-primary/50 font-black ml-1">
                    {language === 'de' ? 'Passwort bestätigen' : 'Confirm Password'}
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="h-14 rounded-2xl bg-brand-primary/5 border-none font-bold text-brand-primary placeholder:text-brand-primary/20 focus-visible:ring-brand-accent"
                  />
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-16 bg-brand-primary hover:bg-brand-accent text-white rounded-2xl text-xs font-black uppercase tracking-[0.4em] shadow-2xl transition-all hover:scale-[1.02] active:scale-[0.98] mt-4"
              >
                {loading ? (
                  <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  mode === "login"
                    ? (language === 'de' ? 'Anmeldung Starten' : 'Start Login')
                    : (language === 'de' ? 'Jetzt Registrieren' : 'Sign Up Now')
                )}
              </Button>
            </form>

            {/* Back to Home */}
            <div className="mt-10 text-center border-t border-brand-primary/5 pt-8">
              <Link
                to="/"
                className="inline-flex items-center text-[10px] font-black uppercase tracking-[0.3em] text-brand-primary/30 hover:text-brand-primary transition-colors"
              >
                <span className="material-symbols-outlined mr-2 text-lg">arrow_back</span>
                {language === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}
              </Link>
            </div>

            {/* Footer */}
            <div className="mt-6 text-center">
              <p className="text-[9px] text-brand-primary/30 uppercase tracking-widest">
                {language === 'de'
                  ? 'Mit der Anmeldung akzeptierst du unsere'
                  : 'By signing in, you agree to our'}
              </p>
              <div className="flex justify-center gap-2 text-[9px] font-black uppercase tracking-widest mt-1">
                <Link to="/datenschutz" className="text-brand-primary/60 hover:text-brand-accent transition-colors">
                  {language === 'de' ? 'Datenschutz' : 'Privacy'}
                </Link>
                <span className="text-brand-primary/10">/</span>
                <Link to="/agb" className="text-brand-primary/60 hover:text-brand-accent transition-colors">
                  {language === 'de' ? 'AGB' : 'Terms'}
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Branding Footer */}
        <p className="text-center mt-8 text-[9px] font-black uppercase tracking-[0.6em] text-brand-primary/20 animate-pulse">
          Premium Event Architecture • Mokiplaner
        </p>
      </motion.div>
    </div >
  );
}
